/**
 * Workstation 模块 API 工厂函数
 * 
 * @module api/workstation/factory
 * @description 工位管理模块的 API 工厂函数
 * 
 * @backend backend/app/Modules/Organization/Controllers/WorkstationController.php
 * @routes /api/v1/workstations
 * 
 * @author YSFL Team
 * @created 2025-10-24
 * @last-updated 2025-10-24
 */

// ===== 导入类型定义 =====
import type {
  Workstation,
  WorkstationQueryParams,
  WorkstationAllQueryParams,
  CreateWorkstationForm,
  UpdateWorkstationForm,
  BatchImportWorkstationForm,
  UpdateOrderForm,
  WorkstationFindByCodeParams,
  UpdateWorkstationStatusForm,
  AssignOperatorForm,
  WorkstationStatistics,
  CalculateCapacityParams,
} from '../../types/organization';

import type { PaginationApiResponse, BatchDeleteResult, BatchImportResult } from '../../types/api';
import type { IRequestClient } from '../core/client';

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes';

/**
 * 创建 Workstation API 实例
 * 
 * @param client HTTP 请求客户端
 * @returns Workstation API 实例
 */
export function createWorkstationApi(client: IRequestClient) {
  return {
    /**
     * 获取工位列表（分页）
     */
    getList(params?: WorkstationQueryParams) {
      return client.get<PaginationApiResponse<Workstation>>(API_ROUTES.workstation.list, { params });
    },

    /**
     * 获取所有工位（不分页）
     */
    getAll(params?: WorkstationAllQueryParams) {
      return client.get<Workstation[]>(API_ROUTES.workstation.all, { params });
    },

    /**
     * 获取工位详情
     */
    getDetail(id: number) {
      return client.get<Workstation>(API_ROUTES.workstation.detail(id));
    },

    /**
     * 创建工位
     */
    create(data: CreateWorkstationForm) {
      return client.post<Workstation>(API_ROUTES.workstation.create, data);
    },

    /**
     * 更新工位
     */
    update(id: number, data: UpdateWorkstationForm) {
      return client.put<Workstation>(API_ROUTES.workstation.workstations(id), data);
    },

    /**
     * 删除工位
     */
    delete(id: number) {
      return client.delete<void>(API_ROUTES.workstation.delete(id));
    },

    /**
     * 批量删除工位
     */
    batchDelete(ids: number[]) {
      return client.post<BatchDeleteResult>(API_ROUTES.workstation.batchDelete, { ids });
    },

    /**
     * 批量导入工位
     */
    import(data: BatchImportWorkstationForm) {
      return client.post<BatchImportResult>(API_ROUTES.workstation.import, data);
    },

    /**
     * 更新工位排序
     */
    updateOrder(data: UpdateOrderForm) {
      return client.put<void>(API_ROUTES.workstation.order, data);
    },

    /**
     * 根据工位编码查询
     */
    findByCode(params: WorkstationFindByCodeParams) {
      return client.get<Workstation>(API_ROUTES.workstation.findByCode, { params });
    },

    /**
     * 根据条码查询工位
     */
    findByBarcode(barcode: string) {
      return client.get<Workstation>(API_ROUTES.workstation.findByBarcode(barcode));
    },

    /**
     * 更新工位状态
     */
    updateStatus(id: number, data: UpdateWorkstationStatusForm) {
      return client.put<Workstation>(API_ROUTES.workstation.update(id), data);
    },

    /**
     * 分配操作员
     */
    assignOperator(id: number, data: AssignOperatorForm) {
      return client.put<Workstation>(API_ROUTES.workstation.assignOperator(id), data);
    },

    /**
     * 清除操作员
     */
    clearOperator(id: number) {
      return client.put<Workstation>(API_ROUTES.workstation.clearOperator(id));
    },

    /**
     * 计算工位理论产能
     */
    calculateCapacity(id: number, params?: CalculateCapacityParams) {
      return client.get<{ stationId: number; workingHours: number; theoreticalCapacity: number }>(
        API_ROUTES.workstation.calculateCapacity(id),
        { params }
      );
    },

    /**
     * 生成工位二维码
     */
    generateQrCode(id: number) {
      return client.get<{ stationId: number; qrCode: string }>(
        API_ROUTES.workstation.qrCode(id)
      );
    },

    /**
     * 获取工位统计信息
     */
    getStatistics(id: number) {
      return client.get<WorkstationStatistics>(API_ROUTES.workstation.statistics(id));
    },
  };
}

/**
 * Workstation API 类型
 */
export type WorkstationApi = ReturnType<typeof createWorkstationApi>;

/**
 * 默认导出
 */
export default createWorkstationApi;

